import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Group } from '@mantine/core';
import { AlertBanner } from '@shipstation/client-common/components/alert-banner';
import { ModalContent } from '@shipstation/client-common/components/modal-content';
import { ModalFooter } from '@shipstation/client-common/components/modal-footer';
import { purchaseShipmentVolume } from '@shipstation/client-common/api/purchase-shipment-volume.api';
import { FormattedMessage, useIntl } from 'react-intl';

import Global from '../../../store/global-selectors';
import { selectReferenceDataShipmentTopupTiers } from '../../../store/reference-data/reference-data.selectors';
import { SHIPMENT_VOLUME_AMOUNT_SELECTED, SHIPMENT_VOLUME_PURCHASED } from '../../../constants/analytics-events';
import { AmountSelect } from './amount-select';
import { UsageOverview } from './usage-overview';
import { useOpenUpgradeDrawer } from './hooks/use-open-upgrade-drawer';
import { useTrackShipmentVolumeEvent } from './hooks/use-track-shipment-volume-event';
import { messages } from '../../purchase-shipment-volume/messages';
import { PaymentMethodSummary } from './payment-method-summary';
import { PaymentMethodChangeView } from './payment-method-change-view';

import type { ShipmentTopupTier } from '@shipstation/client-common/api/shipment-topup-tiers.api';
import type { PurchaseShipmentVolumeModalView } from './purchase-shipment-volume-modal';

const VIEW_PURCHASE = 'purchase' as const;
const VIEW_CHANGE_PAYMENT_METHOD = 'changePaymentMethod' as const;

type PurchaseVolumeContentProps = {
  onClose: () => void;
  onPurchaseSuccess?: () => void;
  isAtLimit: boolean;
  isHighLimitWarning: boolean;
  isMediumLimitWarning: boolean;
  usagePercentage: number;
  totalUsage: number;
  shipmentLimit: number;
  planName: string;
  shipmentPeriodEnd: string | null | undefined;
  initialQuantity?: number | null;
  initialView?: PurchaseShipmentVolumeModalView;
};

export const PurchaseVolumeContent = ({
  onClose,
  onPurchaseSuccess,
  isAtLimit,
  isHighLimitWarning,
  isMediumLimitWarning,
  usagePercentage,
  totalUsage,
  shipmentLimit,
  planName,
  shipmentPeriodEnd,
  initialQuantity,
  initialView,
}: PurchaseVolumeContentProps) => {
  const intl = useIntl();
  const [view, setView] = useState<PurchaseShipmentVolumeModalView>(initialView ?? VIEW_PURCHASE);

  const sellerCurrencyCode = useSelector(Global.selectSellerSubscriptionCurrency);
  const topupTiers: ShipmentTopupTier[] = useSelector(selectReferenceDataShipmentTopupTiers);
  const { handleManagePlan } = useOpenUpgradeDrawer(onClose);
  const trackShipmentVolumeEvent = useTrackShipmentVolumeEvent();

  const [selectedQuantity, setSelectedQuantity] = useState<number | null>(initialQuantity ?? topupTiers[0].quantity ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [partialSuccessMessage, setPartialSuccessMessage] = useState<string | null>(null);

  const selectedTier = topupTiers.find((tier) => tier.quantity === selectedQuantity);
  const purchasePrice = selectedTier ? intl.formatNumber(selectedTier.price, { style: 'currency', currency: sellerCurrencyCode }) : '';

  const handleQuantityChange = useCallback(
    (quantity: number | null) => {
      if (quantity === null) return;
      const tier = topupTiers.find((candidate) => candidate.quantity === quantity);
      trackShipmentVolumeEvent(SHIPMENT_VOLUME_AMOUNT_SELECTED, {
        quantity,
        price: tier?.price,
        currencyCode: sellerCurrencyCode,
      });
      setSelectedQuantity(quantity);
    },
    [topupTiers, sellerCurrencyCode, trackShipmentVolumeEvent],
  );

  const handlePurchase = useCallback(() => {
    if (!selectedQuantity) return;

    setIsSubmitting(true);
    setSubmitError(false);
    setPartialSuccessMessage(null);

    purchaseShipmentVolume({ quantity: selectedQuantity })
      .then(() => {
        trackShipmentVolumeEvent(SHIPMENT_VOLUME_PURCHASED, {
          quantity: selectedQuantity,
          price: selectedTier?.price,
          currencyCode: sellerCurrencyCode,
        });
        onPurchaseSuccess?.();
        onClose();
      })
      .catch((error: unknown) => {
        const responseData = (error as { response?: { data?: unknown } })?.response?.data;
        setPartialSuccessMessage(typeof responseData === 'string' && responseData ? responseData : null);
        setSubmitError(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [onClose, onPurchaseSuccess, selectedQuantity, selectedTier, sellerCurrencyCode, trackShipmentVolumeEvent]);

  const handleChangePaymentMethodClick = useCallback(() => {
    setView(VIEW_CHANGE_PAYMENT_METHOD);
  }, []);

  const handleBackToPurchase = useCallback(() => {
    setView(VIEW_PURCHASE);
  }, []);

  let bodyMessage = messages.purchaseAdditionalShipmentsBody;
  if (isAtLimit) {
    bodyMessage = messages.atLimitBody;
  } else if (isHighLimitWarning || isMediumLimitWarning) {
    bodyMessage = messages.approachingLimitBody;
  }

  if (view === VIEW_CHANGE_PAYMENT_METHOD) {
    return <PaymentMethodChangeView onBack={handleBackToPurchase} selectedQuantity={selectedQuantity} />;
  }

  return (
    <>
      <ModalContent>
        <UsageOverview
          bodyMessage={bodyMessage}
          isHighLimitWarning={isHighLimitWarning}
          isMediumLimitWarning={isMediumLimitWarning}
          isAtLimit={isAtLimit}
          planName={planName}
          shipmentLimit={shipmentLimit}
          totalUsage={totalUsage}
          usagePercentage={usagePercentage}
        />
        <AmountSelect
          currencyCode={sellerCurrencyCode}
          onChange={handleQuantityChange}
          shipmentPeriodEnd={shipmentPeriodEnd ?? null}
          topupTiers={topupTiers}
          value={selectedQuantity}
        />
        <PaymentMethodSummary onChangeClick={handleChangePaymentMethodClick} />
        {submitError ? (
          <AlertBanner description={partialSuccessMessage || <FormattedMessage {...messages.purchaseError} />} status='alert' />
        ) : null}
      </ModalContent>
      <ModalFooter>
        <Group justify='space-between' w='100%'>
          <Button onClick={onClose} variant='ghost'>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Group>
            <Button onClick={handleManagePlan} variant='tertiary'>
              <FormattedMessage {...messages.managePlan} />
            </Button>
            <Button disabled={!selectedQuantity} loading={isSubmitting} onClick={handlePurchase} variant='primary'>
              <FormattedMessage {...messages.purchase} values={{ price: purchasePrice }} />
            </Button>
          </Group>
        </Group>
      </ModalFooter>
    </>
  );
};
